/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * SectionLivePreview — renders a real-time visual preview of any section
 * inside the PropertiesDrawer's left pane. Updates instantly on config change.
 */

import React from 'react';
import { useTheme } from '../../theme/ThemeContext';
import { DEFAULT_HERO_CONFIG } from './editors/HeroEditor';
import { DEFAULT_IMAGE_BANNER_CONFIG } from './editors/ImageBannerEditor';
import { PRESET_DESIGNS, DEFAULT_TOKENS } from './editors/FormBuilderEditor';

// ─── Hero preview ─────────────────────────────────────────────────────────────

const HEADLINE_SIZE = { sm: 18, md: 22, lg: 28, xl: 34 };
const SUB_SIZE      = { sm: 11, md: 13, lg: 15 };
const CONTENT_MAX_W = { narrow: '55%', medium: '75%', wide: '90%', full: '100%' };

function HeadlineText({ cfg }) {
  const text     = cfg.headlineText || '';
  const word     = cfg.headlineHighlightWord || '';
  const style    = cfg.headlineHighlightStyle || 'color';
  const hColor   = cfg.headlineHighlightColor || '#f59e0b';
  const fontSize = HEADLINE_SIZE[cfg.headlineFontSize] || 34;

  const wrapStyle = {
    fontSize,
    fontWeight: 800,
    color: cfg.headlineColor || '#ffffff',
    lineHeight: 1.2,
    fontFamily: cfg.headlineFontFamily || 'inherit',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    marginBottom: 8,
  };

  if (!word || !text.includes(word)) {
    return <div style={wrapStyle}>{text || 'Your headline here'}</div>;
  }

  const parts = text.split(word);
  const hi = (() => {
    if (style === 'underline') return (
      <span style={{ textDecoration: 'underline', textDecorationColor: hColor, textUnderlineOffset: 3 }}>{word}</span>
    );
    if (style === 'mark') return (
      <mark style={{ background: hColor, color: '#fff', borderRadius: 3, padding: '0 3px' }}>{word}</mark>
    );
    return <span style={{ color: hColor }}>{word}</span>;
  })();

  return (
    <div style={wrapStyle}>
      {parts.map((part, i) => (
        <React.Fragment key={i}>{part}{i < parts.length - 1 && hi}</React.Fragment>
      ))}
    </div>
  );
}

function PreviewBtn({ cta, theme, isPrimary }) {
  if (!cta) return null;
  const label = cta.label || (isPrimary ? 'Get Started' : 'Learn More');
  const s = cta.style || (isPrimary ? 'filled' : 'outlined');
  const base = { padding: '6px 13px', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'default', display: 'inline-flex', alignItems: 'center', gap: 4, border: '1.5px solid transparent', userSelect: 'none' };
  const variant = s === 'filled'   ? { background: theme.primary, color: '#fff', borderColor: theme.primary }
    : s === 'outlined' ? { background: 'transparent', color: '#fff', borderColor: 'rgba(255,255,255,0.65)' }
    : s === 'ghost'    ? { background: 'rgba(255,255,255,0.1)', color: '#fff', borderColor: 'transparent' }
    :                    { background: 'transparent', color: '#fff', textDecoration: 'underline', borderColor: 'transparent', padding: '6px 4px' };
  return (
    <div style={{ ...base, ...variant }}>
      {cta.icon && cta.iconPosition !== 'right' && <i className={`bi ${cta.icon}`} style={{ fontSize: 11 }} />}
      {label}
      {cta.icon && cta.iconPosition === 'right' && <i className={`bi ${cta.icon}`} style={{ fontSize: 11 }} />}
    </div>
  );
}

function StarRating({ value }) {
  return (
    <span style={{ color: '#fbbf24', fontSize: 12, letterSpacing: 1 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <i key={i} className={i < Math.round(value) ? 'bi bi-star-fill' : 'bi bi-star'} />
      ))}
    </span>
  );
}

function AvatarStack({ images }) {
  const shown = images.slice(0, 4);
  return (
    <div style={{ display: 'flex' }}>
      {shown.map((src, i) => (
        <div key={i} style={{
          width: 22, height: 22, borderRadius: '50%',
          border: '2px solid rgba(255,255,255,0.7)',
          marginLeft: i > 0 ? -6 : 0,
          background: src ? `url(${src}) center/cover` : '#7c3aed',
          overflow: 'hidden', flexShrink: 0,
        }}>
          {!src && <i className="bi bi-person-fill" style={{ fontSize: 12, color: '#fff', display: 'block', textAlign: 'center', lineHeight: '18px' }} />}
        </div>
      ))}
    </div>
  );
}

function VisualMedia({ cfg, style: extraStyle }) {
  const hasSrc = Boolean(cfg.visualUrl);
  const frame  = cfg.visualDeviceFrame;

  const mediaEl = hasSrc ? (
    <img src={cfg.visualUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: frame === 'phone' ? 10 : 4 }} />
  ) : (
    <div style={{ width: '100%', height: '100%', background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: frame === 'phone' ? 10 : 4 }}>
      <i className={`bi ${cfg.visualType === 'video' ? 'bi-play-circle' : 'bi-image'}`} style={{ fontSize: 28, color: 'rgba(255,255,255,0.5)' }} />
    </div>
  );

  if (frame === 'browser') {
    return (
      <div style={{ borderRadius: 8, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.15)', ...extraStyle }}>
        <div style={{ background: '#2d3748', padding: '5px 10px', display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#fc8181', display: 'inline-block' }} />
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#f6e05e', display: 'inline-block' }} />
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#68d391', display: 'inline-block' }} />
          <div style={{ flex: 1, background: '#1a202c', borderRadius: 3, height: 10, marginLeft: 6 }} />
        </div>
        <div style={{ height: 120 }}>{mediaEl}</div>
      </div>
    );
  }
  if (frame === 'phone') {
    return (
      <div style={{ width: 80, margin: '0 auto', borderRadius: 14, border: '3px solid rgba(255,255,255,0.3)', overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.35)', ...extraStyle }}>
        <div style={{ background: 'rgba(255,255,255,0.15)', height: 6, borderRadius: '0' }} />
        <div style={{ height: 120 }}>{mediaEl}</div>
      </div>
    );
  }
  return (
    <div style={{ borderRadius: 6, overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.25)', ...extraStyle }}>
      <div style={{ height: 130 }}>{mediaEl}</div>
    </div>
  );
}

function HeroPreview({ section }) {
  const { theme } = useTheme();
  const cfg = { ...DEFAULT_HERO_CONFIG, ...(section.section_config || {}) };
  const primaryCTA  = { ...DEFAULT_HERO_CONFIG.primaryCTA,  ...(section.section_config?.primaryCTA  || {}) };
  const secondaryCTA = { ...DEFAULT_HERO_CONFIG.secondaryCTA, ...(section.section_config?.secondaryCTA || {}) };

  const isSplit  = cfg.layoutType?.startsWith('split');
  const isCenter = !isSplit && cfg.textAlignment === 'center';

  // ── background ──
  const bgStyle = {};
  if (cfg.bgType === 'gradient') {
    if (cfg.bgGradientFrom && cfg.bgGradientTo) {
      bgStyle.background = cfg.bgGradientType === 'radial'
        ? `radial-gradient(circle, ${cfg.bgGradientFrom}, ${cfg.bgGradientTo})`
        : `linear-gradient(${cfg.bgGradientAngle ?? 135}deg, ${cfg.bgGradientFrom}, ${cfg.bgGradientTo})`;
    } else {
      bgStyle.background = theme.gradientBackground || `linear-gradient(135deg, ${theme.primary}, #1e293b)`;
    }
  } else if (cfg.bgType === 'color') {
    bgStyle.background = cfg.bgColor || '#1e293b';
  } else if (cfg.bgType === 'image' && cfg.bgImageUrl) {
    bgStyle.backgroundImage = `url(${cfg.bgImageUrl})`;
    bgStyle.backgroundSize = cfg.bgImageSize || 'cover';
    bgStyle.backgroundPosition = cfg.bgImagePosition || 'center';
  } else {
    bgStyle.background = '#1e293b';
  }

  const needsOverlay = cfg.bgType === 'image';
  const subFontSize  = SUB_SIZE[cfg.subheadlineFontSize] || 13;
  const btnJustify   = isCenter ? 'center' : 'flex-start';

  return (
    <div style={{ ...bgStyle, position: 'relative', overflow: 'hidden', borderRadius: 8 }}>
      {needsOverlay && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 0,
          background: cfg.bgOverlayColor || '#000',
          opacity: (cfg.bgOverlayOpacity ?? 40) / 100,
        }} />
      )}

      <div style={{
        position: 'relative', zIndex: 1,
        padding: '24px 20px 20px',
        maxWidth: CONTENT_MAX_W[cfg.contentMaxWidth] || CONTENT_MAX_W.medium,
        margin: '0 auto',
        display: 'flex',
        flexDirection: isSplit ? 'row' : 'column',
        gap: isSplit ? 16 : 0,
        alignItems: isSplit ? 'center' : undefined,
        textAlign: cfg.textAlignment || 'center',
        minHeight: cfg.sectionHeight === 'full-viewport' ? 260 : undefined,
      }}>
        {/* Content column */}
        <div style={{ flex: 1 }}>
          {/* Badge */}
          {cfg.badgeVisible && cfg.badgeText && (
            <div style={{ marginBottom: 10, display: 'flex', justifyContent: isCenter && !isSplit ? 'center' : 'flex-start' }}>
              <span style={{
                display: 'inline-block', fontSize: 10, fontWeight: 700,
                padding: cfg.badgeStyle === 'pill' ? '3px 10px' : '2px 7px',
                borderRadius: cfg.badgeStyle === 'pill' ? 100 : 4,
                background: cfg.badgeStyle === 'outlined' ? 'transparent' : cfg.badgeColor || '#7c3aed',
                border: `1.5px solid ${cfg.badgeColor || '#7c3aed'}`,
                color: cfg.badgeStyle === 'outlined' ? (cfg.badgeColor || '#7c3aed') : '#fff',
              }}>
                {cfg.badgeText}
              </span>
            </div>
          )}

          {/* Headline */}
          <HeadlineText cfg={cfg} />

          {/* Subheadline */}
          {cfg.subheadlineVisible && (
            <div style={{
              fontSize: subFontSize, lineHeight: 1.5, marginBottom: 16,
              color: cfg.subheadlineColor || '#cbd5e1',
              fontFamily: cfg.bodyFontFamily || 'inherit',
            }}>
              {cfg.subheadlineText || 'Your subheadline text here.'}
            </div>
          )}

          {/* CTA row */}
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 8,
            flexDirection: cfg.buttonLayout === 'stacked' ? 'column' : 'row',
            justifyContent: btnJustify,
            alignItems: cfg.buttonLayout === 'stacked' ? (isCenter ? 'center' : 'flex-start') : undefined,
            marginBottom: cfg.socialProofVisible ? 14 : 0,
          }}>
            <PreviewBtn cta={primaryCTA} theme={theme} isPrimary />
            {secondaryCTA.visible && <PreviewBtn cta={secondaryCTA} theme={theme} />}
          </div>

          {/* Social proof */}
          {cfg.socialProofVisible && (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, justifyContent: btnJustify }}>
              {cfg.avatarsVisible && cfg.avatarImages?.length > 0 && <AvatarStack images={cfg.avatarImages} />}
              {cfg.ratingVisible && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <StarRating value={cfg.ratingValue ?? 5} />
                  {cfg.ratingCount && <span style={{ fontSize: 10, color: cfg.headlineColor || '#fff', opacity: 0.75 }}>{cfg.ratingCount}</span>}
                </div>
              )}
              {cfg.userCountText && (
                <span style={{ fontSize: 10, color: cfg.headlineColor || '#fff', opacity: 0.65 }}>{cfg.userCountText}</span>
              )}
            </div>
          )}
        </div>

        {/* Split-layout visual */}
        {isSplit && cfg.visualType !== 'none' && (
          <div style={{ flex: 1 }}>
            <VisualMedia cfg={cfg} />
          </div>
        )}
      </div>

      {/* Visual below content */}
      {!isSplit && cfg.visualType !== 'none' && cfg.visualPosition === 'below' && (
        <div style={{ padding: '0 20px 20px' }}>
          <VisualMedia cfg={cfg} />
        </div>
      )}

      {/* Scroll indicator */}
      {cfg.scrollIndicatorVisible && (
        <div style={{ textAlign: 'center', padding: '0 0 10px', color: 'rgba(255,255,255,0.5)' }}>
          <i className="bi bi-chevron-double-down" style={{ fontSize: 14 }} />
        </div>
      )}

      {/* Shape divider hint */}
      {cfg.shapeDivider !== 'none' && (
        <div style={{
          height: 10, borderRadius: '0 0 8px 8px',
          background: cfg.shapeDividerColor || '#fff',
          opacity: 0.35,
          clipPath: cfg.shapeDivider === 'wave'     ? 'ellipse(55% 100% at 50% 100%)'
                  : cfg.shapeDivider === 'diagonal' ? 'polygon(0 40%, 100% 0, 100% 100%, 0% 100%)'
                  :                                   'ellipse(70% 100% at 50% 100%)',
        }} />
      )}
    </div>
  );
}

// ─── Other section previews ───────────────────────────────────────────────────

function TextBlockPreview({ section }) {
  const cfg = section.section_config || {};

  // Legacy config (title/body/alignment)
  if (!('blockType' in cfg)) {
    return (
      <div style={{ background: '#fff', padding: '20px 18px', borderRadius: 8, textAlign: cfg.alignment || 'left' }}>
        {cfg.title && <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b', marginBottom: 8 }}>{cfg.title}</div>}
        <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>{cfg.body || 'Your content goes here.'}</div>
      </div>
    );
  }

  // Rich config preview
  const isList  = cfg.blockType === 'list';
  const isQuote = cfg.blockType === 'quote';

  const textStyle = {
    fontFamily:    cfg.fontFamily ? `"${cfg.fontFamily}", sans-serif` : 'inherit',
    fontWeight:    cfg.fontWeight || 400,
    fontStyle:     cfg.fontStyle  || 'normal',
    fontSize:      Math.min(cfg.fontSize || 16, 28),
    lineHeight:    cfg.lineHeight || 1.7,
    letterSpacing: cfg.letterSpacing ? `${cfg.letterSpacing}em` : 'normal',
    textTransform: cfg.textTransform || 'none',
    textDecoration: cfg.textDecoration || 'none',
    textAlign:     cfg.textAlign || 'left',
    color:         cfg.textColor || '#374151',
    margin:        0,
  };

  const wrapStyle = {
    borderRadius: 8,
    padding:      '18px 16px',
    background:   cfg.bgVisible ? cfg.bgColor : '#fff',
  };

  if (cfg.borderVisible) {
    const side = cfg.borderSide || 'all';
    const b = `${cfg.borderWidth || 1}px ${cfg.borderStyle || 'solid'} ${cfg.borderColor || '#e2e8f0'}`;
    if (side === 'all') wrapStyle.border = b;
    else wrapStyle[`border${side.charAt(0).toUpperCase() + side.slice(1)}`] = b;
    if (cfg.borderRadius) wrapStyle.borderRadius = cfg.borderRadius;
  }

  if (isQuote) {
    const accent = cfg.quoteAccentColor || '#7c3aed';
    if (cfg.quoteStyle === 'left-border') wrapStyle.borderLeft = `4px solid ${accent}`;
    else if (cfg.quoteStyle === 'card') Object.assign(wrapStyle, { background: '#f8fafc', borderLeft: `4px solid ${accent}` });
    return (
      <div style={wrapStyle}>
        <div style={{ ...textStyle, fontStyle: 'italic' }} dangerouslySetInnerHTML={{ __html: cfg.text || '<p>Quote text…</p>' }} />
        {cfg.quoteAuthorVisible && cfg.quoteAuthor && (
          <div style={{ fontSize: 11, color: cfg.textColor, opacity: 0.7, marginTop: 8, fontStyle: 'normal' }}>
            — <strong>{cfg.quoteAuthor}</strong>{cfg.quoteAuthorRole ? `, ${cfg.quoteAuthorRole}` : ''}
          </div>
        )}
      </div>
    );
  }

  if (isList) {
    const items = (cfg.listItems || []).slice(0, 5);
    const icon = cfg.listStyle === 'check' ? '✓' : cfg.listStyle === 'arrow' ? '→' : cfg.listStyle === 'numbered' ? null : '•';
    return (
      <div style={wrapStyle}>
        {items.map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6 }}>
            <span style={{ color: cfg.listIconColor || cfg.textColor || '#7c3aed', fontSize: 13, flexShrink: 0, marginTop: 1 }}>
              {icon !== null ? icon : `${i + 1}.`}
            </span>
            <span style={{ ...textStyle, fontSize: Math.min(cfg.fontSize || 16, 14) }}>{item}</span>
          </div>
        ))}
      </div>
    );
  }

  const Tag = cfg.htmlTag && ['h1','h2','h3','h4','h5','p','span','blockquote'].includes(cfg.htmlTag) ? cfg.htmlTag : 'p';
  return (
    <div style={wrapStyle}>
      <Tag style={textStyle} dangerouslySetInnerHTML={{ __html: cfg.text || '<p>Your text here.</p>' }} />
    </div>
  );
}

function ImageBannerPreview({ section }) {
  const { theme } = useTheme();
  const cfg = { ...DEFAULT_IMAGE_BANNER_CONFIG, ...(section.section_config || {}) };
  const primaryCTA   = { ...DEFAULT_IMAGE_BANNER_CONFIG.primaryCTA,   ...(section.section_config?.primaryCTA   || {}) };
  const secondaryCTA = { ...DEFAULT_IMAGE_BANNER_CONFIG.secondaryCTA, ...(section.section_config?.secondaryCTA || {}) };

  // Background
  const bgStyle = {};
  if (cfg.imageUrl) {
    const bgPos = cfg.imagePosition === 'custom'
      ? `${cfg.imagePositionX}% ${cfg.imagePositionY}%`
      : cfg.imagePosition || 'center';
    bgStyle.backgroundImage  = `url(${cfg.imageUrl})`;
    bgStyle.backgroundSize   = cfg.imageFit === 'contain' ? 'contain' : cfg.imageFit === 'none' ? 'auto' : 'cover';
    bgStyle.backgroundPosition = bgPos;
    bgStyle.backgroundRepeat = cfg.imageRepeat || 'no-repeat';
  } else {
    bgStyle.background = theme.gradientBackground || `linear-gradient(135deg, ${theme.primary}, #1e293b)`;
  }

  // Overlay
  let overlayBg;
  if (cfg.overlayVisible) {
    if (cfg.overlayType === 'gradient') {
      overlayBg = cfg.overlayGradientType === 'radial'
        ? `radial-gradient(circle, ${cfg.overlayGradientFrom}, ${cfg.overlayGradientTo})`
        : `linear-gradient(${cfg.overlayGradientAngle}deg, ${cfg.overlayGradientFrom}, ${cfg.overlayGradientTo})`;
    } else {
      overlayBg = cfg.overlayColor;
    }
  }

  // Text position mapping
  const textPosMap = {
    'centered':      { top: '50%', left: '50%', transform: 'translate(-50%,-50%)' },
    'bottom-left':   { bottom: 12, left: 14 },
    'bottom-center': { bottom: 12, left: '50%', transform: 'translateX(-50%)' },
    'bottom-right':  { bottom: 12, right: 14 },
    'top-left':      { top: 12, left: 14 },
    'top-center':    { top: 12, left: '50%', transform: 'translateX(-50%)' },
    'top-right':     { top: 12, right: 14 },
    'center-left':   { top: '50%', left: 14, transform: 'translateY(-50%)' },
    'center-right':  { top: '50%', right: 14, transform: 'translateY(-50%)' },
    'custom':        { top: `${cfg.textOverlayPositionY}%`, left: `${cfg.textOverlayPositionX}%`, transform: 'translate(-50%,-50%)' },
  };
  const textPos = textPosMap[cfg.textOverlayLayout] || textPosMap['centered'];

  // Border radius
  const borderRadius = cfg.borderRadiusPerCorner
    ? `${cfg.borderRadiusTopLeft}px ${cfg.borderRadiusTopRight}px ${cfg.borderRadiusBottomRight}px ${cfg.borderRadiusBottomLeft}px`
    : `${cfg.borderRadius || 0}px`;

  // Box shadow
  const boxShadow = cfg.boxShadow === 'sm' ? '0 1px 4px rgba(0,0,0,0.12)'
    : cfg.boxShadow === 'md' ? '0 4px 16px rgba(0,0,0,0.15)'
    : cfg.boxShadow === 'lg' ? '0 8px 32px rgba(0,0,0,0.18)'
    : cfg.boxShadow === 'custom' ? `${cfg.boxShadowX}px ${cfg.boxShadowY}px ${cfg.boxShadowBlur}px ${cfg.boxShadowSpread}px ${cfg.boxShadowColor}40`
    : 'none';

  const textAlign = cfg.textAlignment || 'center';
  const btnJustify = textAlign === 'center' ? 'center' : textAlign === 'right' ? 'flex-end' : 'flex-start';

  return (
    <div style={{ ...bgStyle, position: 'relative', overflow: 'hidden', minHeight: 160, borderRadius, boxShadow }}>
      {/* Overlay */}
      {cfg.overlayVisible && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 1, background: overlayBg, opacity: cfg.overlayOpacity / 100, mixBlendMode: cfg.overlayBlendMode || 'normal' }} />
      )}

      {/* Text overlay */}
      {cfg.textOverlayVisible && (
        <div style={{ position: 'absolute', zIndex: 2, maxWidth: '80%', textAlign, ...textPos }}>
          {cfg.badgeVisible && cfg.badgeText && (
            <div style={{ display: 'inline-block', marginBottom: 6, fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: cfg.badgeColor || '#7c3aed', color: '#fff' }}>
              {cfg.badgeText}
            </div>
          )}
          <div style={{ fontWeight: cfg.headlineFontWeight || 700, fontSize: Math.min(cfg.headlineFontSize || 40, 22), color: cfg.headlineColor || '#fff', lineHeight: 1.2, fontFamily: cfg.headlineFontFamily ? `"${cfg.headlineFontFamily}", sans-serif` : 'inherit', textShadow: cfg.textShadow ? `0 2px ${cfg.textShadowBlur || 8}px ${cfg.textShadowColor || '#000'}` : 'none', marginBottom: 4 }}>
            {cfg.headlineText || 'Your headline here'}
          </div>
          {cfg.subheadlineVisible && cfg.subheadlineText && (
            <div style={{ fontSize: Math.min(cfg.subheadlineFontSize || 18, 12), color: cfg.subheadlineColor || '#e2e8f0', lineHeight: 1.4, marginBottom: cfg.ctaVisible ? 8 : 0 }}>
              {cfg.subheadlineText}
            </div>
          )}
          {cfg.ctaVisible && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexDirection: cfg.ctaLayout === 'stacked' ? 'column' : 'row', justifyContent: btnJustify, alignItems: cfg.ctaLayout === 'stacked' ? btnJustify : undefined }}>
              <div style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 4, background: primaryCTA.color || theme.primary, color: primaryCTA.textColor || '#fff', fontSize: 10, fontWeight: 600 }}>
                {primaryCTA.label || 'Get Started'}
              </div>
              {secondaryCTA.visible && (
                <div style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 4, border: `1.5px solid ${secondaryCTA.color || '#fff'}`, color: secondaryCTA.textColor || '#fff', fontSize: 10, fontWeight: 600 }}>
                  {secondaryCTA.label || 'Learn More'}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Placeholder when no image and no text overlay */}
      {!cfg.imageUrl && !cfg.textOverlayVisible && (
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 160, color: 'rgba(255,255,255,0.45)', flexDirection: 'column', gap: 6 }}>
          <i className="bi bi-image" style={{ fontSize: 28 }} />
          <span style={{ fontSize: 11 }}>Image Banner</span>
        </div>
      )}
    </div>
  );
}

function DividerPreview({ section }) {
  const { theme } = useTheme();
  const cfg = section.section_config || {};
  if (cfg.style === 'blank') return <div style={{ height: 40, background: '#f8fafc', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontSize: 10, color: '#cbd5e1' }}>Blank spacer</span></div>;
  const lineColor = cfg.color || theme.primary;
  const borderStyle = cfg.style === 'dashed' ? 'dashed' : cfg.style === 'dotted' ? 'dotted' : 'solid';
  return (
    <div style={{ padding: '20px 18px', background: '#fff', borderRadius: 8, display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: `${cfg.width || 80}%`, borderTop: `${cfg.thickness || 1}px ${borderStyle} ${lineColor}${cfg.style === 'gradient' ? '' : ''}` }} />
    </div>
  );
}

// ─── Form Builder preview helpers ────────────────────────────────────────────

function resolveFormTokens(cfg) {
  const id = cfg.activeDesignId || 'default';
  const preset = PRESET_DESIGNS.find(d => d.designId === id);
  const custom = (cfg.designs || []).find(d => d.designId === id);
  const base = { ...DEFAULT_TOKENS, ...(preset ? preset.tokens : {}), ...(custom ? custom.tokens : {}) };

  // Apply Container Styling cfg-level overrides — mirrors FormBuilderSection.js resolveTokens
  if (cfg.cancelStyle)                       base.cancelButtonStyle = cfg.cancelStyle;
  if (cfg.form_bg_color)                     base.formBgColor       = cfg.form_bg_color;
  if (cfg.form_bg_opacity !== undefined)     base.formBgOpacity     = cfg.form_bg_opacity;
  if (cfg.form_border_visible !== undefined) base.formBorderVisible = cfg.form_border_visible;
  if (cfg.form_border_color)                 base.formBorderColor   = cfg.form_border_color;
  if (cfg.form_border_width !== undefined)   base.formBorderWidth   = cfg.form_border_width;
  if (cfg.form_border_radius !== undefined)  base.formBorderRadius  = cfg.form_border_radius;
  if (cfg.form_border_style)                 base.formBorderStyle   = cfg.form_border_style;
  if (cfg.form_shadow)                       base.formShadow        = cfg.form_shadow;
  if (cfg.form_padding_x !== undefined)      base.formPaddingX      = cfg.form_padding_x;
  if (cfg.form_padding_y !== undefined)      base.formPaddingY      = cfg.form_padding_y;
  if (cfg.form_max_width !== undefined)      base.formMaxWidth      = cfg.form_max_width;
  if (cfg.field_gap !== undefined)           base.fieldGap          = cfg.field_gap;
  if (cfg.row_gap !== undefined)             base.rowGap            = cfg.row_gap;

  return base;
}

function resolveOuterPadding(cfg) {
  if (cfg.padding === 'custom') {
    const y = cfg.section_padding_y ?? 0;
    const x = cfg.section_padding_x ?? 0;
    return `${y}px ${x}px`;
  }
  const PADDING_MAP = { none: '0', sm: '4px 6px', md: '8px 12px', lg: '16px 20px', xl: '24px 28px' };
  return PADDING_MAP[cfg.padding] || '0';
}

function resolveOuterMaxWidth(cfg) {
  if (cfg.form_width === 'custom') {
    return cfg.section_max_width ? `${cfg.section_max_width}px` : '100%';
  }
  const WIDTH_MAP = { narrow: '400px', normal: '600px', wide: '800px', full: '100%' };
  return WIDTH_MAP[cfg.form_width] || '100%';
}

function getAllFormFields(cfg) {
  if (cfg.stagesEnabled && (cfg.stages || []).length > 0) {
    return cfg.stages.flatMap(s => (s.rows || []).flatMap(r => r.fields || []));
  }
  if (cfg.rowLayoutEnabled && (cfg.rows || []).length > 0) {
    return (cfg.rows || []).flatMap(r => r.fields || []);
  }
  return cfg.fields || [];
}

function FormFieldMock({ field, tokens, colSpan, cols }) {
  const t = tokens;
  const isCheck  = field.type === 'checkbox' || field.type === 'radio';
  const isArea   = field.type === 'textarea';
  const isFloat  = t.labelPosition === 'floating';
  const isMat    = t.inputBorderWidth === 0;

  const inputBox = {
    height: isArea ? 28 : Math.min(t.inputHeight, 28),
    background: t.inputBgColor,
    border: isMat ? 'none' : `${t.inputBorderWidth}px ${t.inputBorderStyle} ${t.inputBorderColor}`,
    borderBottom: isMat ? `1.5px solid ${t.inputBorderColor}` : undefined,
    borderRadius: isMat ? 0 : Math.min(t.inputBorderRadius, 8),
    width: '100%',
  };

  return (
    <div style={{
      gridColumn: field.fullWidth ? '1 / -1' : colSpan > 1 ? `span ${Math.min(colSpan, cols)}` : 'span 1',
    }}>
      {!isCheck && !isFloat && (
        <div style={{ fontSize: 9, fontWeight: t.labelFontWeight, color: t.labelColor, marginBottom: 2, lineHeight: 1.2 }}>
          {field.label}{field.required && <span style={{ color: t.requiredMarkColor }}> *</span>}
        </div>
      )}
      {isCheck ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 11, height: 11, borderRadius: field.type === 'radio' ? '50%' : 2, border: `1.5px solid ${t.inputBorderColor}`, background: t.inputBgColor, flexShrink: 0 }} />
          <div style={{ fontSize: 9, color: t.labelColor }}>{field.label}</div>
        </div>
      ) : (
        <div style={inputBox} />
      )}
    </div>
  );
}

function FormBuilderPreview({ section }) {
  const { theme } = useTheme();
  const cfg = section.section_config || {};
  const t   = resolveFormTokens(cfg);

  const isStaged     = cfg.stagesEnabled && (cfg.stages || []).length > 0;
  const isRowLayout  = cfg.rowLayoutEnabled;
  const stages       = cfg.stages || [];
  const rows         = cfg.rows   || [];
  const flatFields   = cfg.fields || [];
  const allFields    = getAllFormFields(cfg);
  const totalFields  = allFields.length;

  const shadowVal = (s) => s === 'sm' ? '0 1px 4px rgba(0,0,0,0.07)' : s === 'md' ? '0 4px 16px rgba(0,0,0,0.10)' : s === 'lg' ? '0 8px 24px rgba(0,0,0,0.14)' : 'none';

  const containerStyle = {
    background: t.formBgColor,
    borderRadius: Math.min(t.formBorderRadius, 14),
    padding: `${Math.min(t.formPaddingY, 16)}px ${Math.min(t.formPaddingX, 16)}px`,
    border: t.formBorderVisible ? `${t.formBorderWidth}px solid ${t.formBorderColor}` : '1px solid #f0f0f0',
    boxShadow: shadowVal(t.formShadow),
  };

  // Decide which rows to show in the preview (first stage if staged, root rows otherwise)
  const previewRows = isStaged && stages.length > 0
    ? (stages[0].rows || [])
    : isRowLayout ? rows : null;

  const MAX_PREVIEW_FIELDS = 5;

  const renderRows = (rowList) => {
    let rendered = 0;
    const nodes = [];
    for (let ri = 0; ri < rowList.length && rendered < MAX_PREVIEW_FIELDS; ri++) {
      const row = rowList[ri];
      const cols = row.fieldsPerRow || 1;
      const rowFields = (row.fields || []).filter(() => rendered < MAX_PREVIEW_FIELDS);
      if (!rowFields.length) continue;
      nodes.push(
        <div key={row.rowId || ri} style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gap: `${Math.min(t.fieldGap, 8)}px ${Math.min(row.gap || 8, 8)}px`,
          marginBottom: 8,
        }}>
          {rowFields.map(f => {
            rendered++;
            return <FormFieldMock key={f.id} field={f} tokens={t} colSpan={f.columnSpan || 1} cols={cols} />;
          })}
        </div>
      );
    }
    return nodes;
  };

  const renderFlatFields = (fieldList) =>
    fieldList.slice(0, MAX_PREVIEW_FIELDS).map(f => (
      <div key={f.id} style={{ marginBottom: 8 }}>
        <FormFieldMock field={f} tokens={t} colSpan={1} cols={1} />
      </div>
    ));

  const hasContent = totalFields > 0;

  const outerStyle = {
    background: cfg.bg_color || 'transparent',
    padding: resolveOuterPadding(cfg),
    width: '100%',
    maxWidth: resolveOuterMaxWidth(cfg),
    margin: '0 auto',
  };

  return (
    <div style={outerStyle}>
    <div style={containerStyle}>
      {/* Stage progress bar (mini) */}
      {isStaged && stages.length > 1 && cfg.progressIndicatorStyle !== 'none' && (
        <div style={{ marginBottom: 10 }}>
          {cfg.progressIndicatorStyle === 'bar' ? (
            <div style={{ height: 4, background: t.progressBgColor, borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${(1 / stages.length) * 100}%`, background: cfg.progressActiveColor || theme.primary, borderRadius: 2 }} />
            </div>
          ) : cfg.progressIndicatorStyle === 'dots' ? (
            <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
              {stages.map((_, i) => (
                <div key={i} style={{
                  width: i === 0 ? 14 : 6, height: 6, borderRadius: 3,
                  background: i === 0 ? (cfg.progressActiveColor || theme.primary) : (cfg.progressInactiveColor || '#e2e8f0'),
                }} />
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {stages.map((s, i) => (
                <React.Fragment key={s.stageId || i}>
                  <div style={{
                    width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                    background: i === 0 ? (cfg.progressActiveColor || theme.primary) : (cfg.progressInactiveColor || '#e2e8f0'),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 8, fontWeight: 700, color: i === 0 ? '#fff' : '#94a3b8',
                  }}>{i + 1}</div>
                  {i < stages.length - 1 && cfg.progressConnectorVisible !== false && (
                    <div style={{ flex: 1, height: 2, background: cfg.progressInactiveColor || '#e2e8f0', minWidth: 6 }} />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Title */}
      {cfg.form_title && (
        <div style={{ fontWeight: 700, fontSize: 12, color: t.inputTextColor || '#1e293b', marginBottom: 10 }}>{cfg.form_title}</div>
      )}

      {/* Stage label */}
      {isStaged && stages[0]?.stageLabel && (
        <div style={{ fontSize: 10, fontWeight: 600, color: t.labelColor, marginBottom: 8, opacity: 0.8 }}>
          {stages[0].stageLabel}
        </div>
      )}

      {/* Fields */}
      {!hasContent ? (
        <div style={{ textAlign: 'center', padding: '10px 0', color: '#cbd5e1' }}>
          <i className="bi bi-ui-checks" style={{ fontSize: 22, display: 'block', marginBottom: 4 }} />
          <span style={{ fontSize: 10 }}>No fields yet</span>
        </div>
      ) : previewRows ? (
        <>
          {renderRows(previewRows)}
          {totalFields > MAX_PREVIEW_FIELDS && (
            <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 2 }}>+{totalFields - MAX_PREVIEW_FIELDS} more fields</div>
          )}
        </>
      ) : (
        <>
          {renderFlatFields(flatFields)}
          {flatFields.length > MAX_PREVIEW_FIELDS && (
            <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 2 }}>+{flatFields.length - MAX_PREVIEW_FIELDS} more fields</div>
          )}
        </>
      )}

      {/* Submit button */}
      <div style={{ marginTop: 10 }}>
        <div style={{
          display: t.submitFullWidth ? 'block' : 'inline-block',
          padding: `${Math.min(t.submitPaddingY, 8)}px ${Math.min(t.submitPaddingX, 16)}px`,
          borderRadius: Math.min(t.submitBorderRadius, 10),
          background: cfg.button_color || t.submitBgColor,
          color: cfg.button_text_color || t.submitTextColor,
          fontSize: 10, fontWeight: t.submitFontWeight,
          textAlign: 'center',
          boxShadow: t.submitShadow !== 'none' ? shadowVal(t.submitShadow) : 'none',
        }}>
          {cfg.button_text || 'Submit'}
        </div>
      </div>

      {/* Staged nav hint */}
      {isStaged && stages.length > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, gap: 6 }}>
          <div style={{ fontSize: 9, color: '#94a3b8', background: t.navButtonBgColor, borderRadius: 4, padding: '3px 8px' }}>
            {stages[0]?.backLabel || 'Back'}
          </div>
          <div style={{ fontSize: 9, color: '#fff', background: t.submitBgColor, borderRadius: 4, padding: '3px 8px' }}>
            {stages[0]?.nextLabel || 'Next'} →
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

function StatWidgetsPreview({ section }) {
  const { theme } = useTheme();
  const cfg     = section.section_config || {};
  const widgets = (cfg.widgets || []).slice(0, 4);
  return (
    <div style={{ background: '#f8fafc', borderRadius: 8, padding: '14px 12px' }}>
      {widgets.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '12px 0', color: '#cbd5e1' }}>
          <i className="bi bi-bar-chart-line" style={{ fontSize: 24, display: 'block', marginBottom: 6 }} />
          <span style={{ fontSize: 11 }}>No widgets yet</span>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(widgets.length, 2)}, 1fr)`, gap: 8 }}>
          {widgets.map(w => (
            <div key={w.id} style={{ background: '#fff', borderRadius: 6, padding: '10px 10px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', borderLeft: `3px solid ${theme.primary}` }}>
              {w.icon && <i className={`bi ${w.icon}`} style={{ color: theme.primary, fontSize: 14 }} />}
              <div style={{ fontSize: 16, fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>{w.value || '—'}</div>
              <div style={{ fontSize: 10, color: '#94a3b8' }}>{w.label || 'Metric'}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DataSectionPreview({ section }) {
  const { theme } = useTheme();
  const type   = section.section_type;
  const cfg    = section.section_config || {};
  const binding = section.data_binding  || {};

  const isCards    = type === 'data_cards';
  const isTable    = type === 'data_table';
  const isList     = type === 'data_list';
  const isTimeline = type === 'data_timeline';

  const iconMap = { data_cards: 'bi-grid', data_table: 'bi-table', data_list: 'bi-list-ul', data_timeline: 'bi-clock-history' };
  const slug = binding.table_slug || '';

  const SkeletonLine = ({ w = 100, h = 8, style }) => (
    <div style={{ height: h, width: `${w}%`, background: '#e2e8f0', borderRadius: 3, ...style }} />
  );

  return (
    <div style={{ background: '#fff', borderRadius: 8, padding: '14px 12px', border: '1px solid #e2e8f0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <i className={`bi ${iconMap[type] || 'bi-grid'}`} style={{ color: theme.primary, fontSize: 14 }} />
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#374151' }}>{type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</div>
          {slug ? (
            <div style={{ fontSize: 10, color: theme.primary }}>→ {slug}</div>
          ) : (
            <div style={{ fontSize: 10, color: '#f59e0b' }}>No table bound</div>
          )}
        </div>
      </div>

      {isCards && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
          {[0,1,2].map(i => (
            <div key={i} style={{ background: '#f8fafc', borderRadius: 6, padding: '8px 8px', border: '1px solid #e2e8f0' }}>
              <div style={{ height: 40, background: '#e2e8f0', borderRadius: 4, marginBottom: 6 }} />
              <SkeletonLine w={80} h={7} style={{ marginBottom: 4 }} />
              <SkeletonLine w={60} h={6} />
            </div>
          ))}
        </div>
      )}

      {isTable && (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 5, overflow: 'hidden' }}>
          <div style={{ background: '#f8fafc', padding: '6px 8px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            {[70, 60, 50].map((w, i) => <SkeletonLine key={i} w={w} h={7} style={{ background: '#cbd5e1' }} />)}
          </div>
          {[0,1,2].map(r => (
            <div key={r} style={{ padding: '5px 8px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, borderTop: '1px solid #f1f5f9' }}>
              {[90, 55, 70].map((w, i) => <SkeletonLine key={i} w={w} h={6} />)}
            </div>
          ))}
        </div>
      )}

      {isList && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {[0,1,2,3].map(i => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ width: 28, height: 28, borderRadius: 4, background: '#e2e8f0', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <SkeletonLine w={75} h={7} style={{ marginBottom: 4 }} />
                <SkeletonLine w={50} h={5} />
              </div>
            </div>
          ))}
        </div>
      )}

      {isTimeline && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0, paddingLeft: 12, borderLeft: `2px solid ${theme.primary}30` }}>
          {[0,1,2].map(i => (
            <div key={i} style={{ position: 'relative', paddingBottom: 12, paddingLeft: 12 }}>
              <div style={{ position: 'absolute', left: -17, top: 3, width: 10, height: 10, borderRadius: '50%', background: theme.primary, border: '2px solid #fff' }} />
              <SkeletonLine w={40} h={6} style={{ marginBottom: 4 }} />
              <SkeletonLine w={80} h={7} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ChartPreview({ section }) {
  const { theme } = useTheme();
  const type = section.section_type;
  const isBar = type === 'chart_bar';
  const bars = [65, 40, 80, 55, 70, 45, 90];

  return (
    <div style={{ background: '#fff', borderRadius: 8, padding: '14px 12px', border: '1px solid #e2e8f0' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 10 }}>
        <i className={`bi ${isBar ? 'bi-bar-chart-fill' : 'bi-graph-up'} me-1`} style={{ color: theme.primary }} />
        {isBar ? 'Bar Chart' : 'Line Chart'} Preview
      </div>
      {isBar ? (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, height: 70, padding: '0 4px' }}>
          {bars.map((h, i) => (
            <div key={i} style={{ flex: 1, height: `${h}%`, background: i % 2 === 0 ? theme.primary : theme.primary + '80', borderRadius: '3px 3px 0 0' }} />
          ))}
        </div>
      ) : (
        <svg width="100%" height="70" viewBox="0 0 280 70">
          <polyline
            points={bars.map((h, i) => `${(i * 280) / 6},${70 - h * 0.7}`).join(' ')}
            fill="none" stroke={theme.primary} strokeWidth="2.5" strokeLinejoin="round"
          />
          <polyline
            points={[`0,70`, ...bars.map((h, i) => `${(i * 280) / 6},${70 - h * 0.7}`), `280,70`].join(' ')}
            fill={theme.primary + '20'} stroke="none"
          />
        </svg>
      )}
      <div style={{ display: 'flex', gap: 3, marginTop: 4 }}>
        {bars.map((_, i) => <div key={i} style={{ flex: 1, height: 4, background: '#f1f5f9', borderRadius: 2 }} />)}
      </div>
    </div>
  );
}

function HtmlCssPreview({ section }) {
  const { theme } = useTheme();
  const cfg = section.section_config || {};
  const hasContent = cfg.html || cfg.css || cfg.js;
  return (
    <div style={{ background: '#0f172a', borderRadius: 8, padding: '16px', minHeight: 100 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <i className="bi bi-code-slash" style={{ color: theme.primary, fontSize: 16 }} />
        <span style={{ color: '#e2e8f0', fontSize: 12, fontWeight: 600 }}>HTML / CSS / JS Workspace</span>
      </div>
      {hasContent ? (
        <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#94a3b8', lineHeight: 1.6 }}>
          {cfg.html && <div><span style={{ color: '#7dd3fc' }}>{'<'}</span><span style={{ color: '#86efac' }}>{'html'}</span><span style={{ color: '#7dd3fc' }}>{'>'}</span>{' '}<span style={{ color: '#94a3b8', fontStyle: 'italic' }}>({cfg.html.length} chars)</span></div>}
          {cfg.css  && <div><span style={{ color: '#c4b5fd' }}>{'/* css */'}</span>{' '}<span style={{ color: '#94a3b8', fontStyle: 'italic' }}>({cfg.css.length} chars)</span></div>}
          {cfg.js   && <div><span style={{ color: '#fde68a' }}>{'// js'}</span>{' '}<span style={{ color: '#94a3b8', fontStyle: 'italic' }}>({cfg.js.length} chars)</span></div>}
        </div>
      ) : (
        <div style={{ color: '#475569', fontSize: 11, textAlign: 'center', marginTop: 12 }}>Open workspace to edit &amp; preview</div>
      )}
    </div>
  );
}

function PublicPagePreview({ section }) {
  const { theme } = useTheme();
  const cfg = section.section_config || {};
  return (
    <div style={{ background: '#f8fafc', borderRadius: 8, padding: '20px 16px', textAlign: 'center', border: '1px dashed #e2e8f0' }}>
      <i className="bi bi-window-stack" style={{ fontSize: 28, color: theme.primary, display: 'block', marginBottom: 8 }} />
      <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4 }}>Public Page Embed</div>
      {cfg.table_slug ? (
        <div style={{ fontSize: 11, color: theme.primary }}>→ {cfg.table_slug}</div>
      ) : (
        <div style={{ fontSize: 11, color: '#94a3b8' }}>No table selected</div>
      )}
    </div>
  );
}

function DefaultPreview({ section }) {
  const { theme } = useTheme();
  const label = section.section_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  return (
    <div style={{ background: '#f8fafc', borderRadius: 8, padding: '32px 16px', textAlign: 'center', border: '1px dashed #e2e8f0' }}>
      <i className="bi bi-puzzle" style={{ fontSize: 28, color: theme.primary + '80', display: 'block', marginBottom: 8 }} />
      <div style={{ fontSize: 12, color: '#94a3b8' }}>{label}</div>
    </div>
  );
}

// ─── Registry ─────────────────────────────────────────────────────────────────

/**
 * A component's real content lives on another page and is stitched in by the
 * server at request time, so there is nothing local to draw here. Naming the
 * reference is more honest than rendering a guess that could disagree with what
 * visitors actually see.
 */
function ComponentPreview({ section }) {
  const ref = (section.section_config || {}).component_ref;
  return (
    <div style={{ padding: 20, textAlign: 'center', color: '#475569' }}>
      <i className="bi bi-puzzle" style={{ fontSize: 26, color: '#7c3aed' }} />
      <div style={{ fontWeight: 700, marginTop: 8, fontSize: 13 }}>
        {ref ? <code>{ref}</code> : 'No component selected'}
      </div>
      <div style={{ fontSize: 11, marginTop: 6, color: '#64748b' }}>
        {ref
          ? 'Rendered from the component when this page is served.'
          : 'Pick a component in the panel on the right.'}
      </div>
    </div>
  );
}

const PREVIEW_MAP = {
  component:          ComponentPreview,
  hero:               HeroPreview,
  text_block:         TextBlockPreview,
  image_banner:       ImageBannerPreview,
  divider:            DividerPreview,
  form_builder:       FormBuilderPreview,
  stat_widgets:       StatWidgetsPreview,
  data_cards:         DataSectionPreview,
  data_table:         DataSectionPreview,
  data_list:          DataSectionPreview,
  data_timeline:      DataSectionPreview,
  chart_bar:          ChartPreview,
  chart_line:         ChartPreview,
  html_css_workspace: HtmlCssPreview,
  public_page:        PublicPagePreview,
};

// ─── Main export ─────────────────────────────────────────────────────────────

export default function SectionLivePreview({ section }) {
  const { theme } = useTheme();
  if (!section) return null;

  const PreviewComponent = PREVIEW_MAP[section.section_type] || DefaultPreview;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{
        padding: '12px 14px 10px',
        borderBottom: '1px solid #e2e8f0',
        background: '#f8fafc',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 7,
      }}>
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: theme.primary,
          boxShadow: `0 0 0 3px ${theme.primary}30`,
          animation: 'pulse 2s infinite',
          flexShrink: 0,
        }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: '#374151', letterSpacing: '0.03em' }}>
          Live Preview
        </span>
        <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 2 }}>
          — {section.section_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
        </span>
      </div>

      {/* Preview scroll area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 12px', background: '#eef2f7' }}>
        {/* Outer "page frame" */}
        <div style={{
          background: '#fff',
          borderRadius: 10,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          overflow: 'hidden',
          marginBottom: 12,
        }}>
          <PreviewComponent section={section} />
        </div>

        <div style={{ fontSize: 10, color: '#94a3b8', textAlign: 'center' }}>
          Preview updates as you edit
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}
